import { useCallback, useEffect } from 'react';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse } from '@iwoca/lapi-client/introducer_portal';
import { Accordion, Pill, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import { useFileUploadReducer } from 'components/Signup/hooks/useFileUploadReducer';
import { useLocale } from 'hooks/useLocale';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import {
  TMarkFeedbackAsDoneItem,
  useGetDocuments,
  useGetIntroducerApplication,
  useMarkFeedbackAsDone,
} from 'lendingApi/lendingApiHooks';
import _ from 'lodash';
import { FileStatusGroup } from '../FileStatusGroup/FileStatusGroup';
import { UploadDropButton } from '../UploadDropButton/UploadDropButton';
import styles from './DocumentGroup.module.css';
import {
  TDocumentTypeWithDE,
  FILE_STATUS_GROUPS,
  getInstruction,
  getRequirementsTitle,
  handleDocumentTypeMapping,
} from './DocumentGroupHelper';
import { LinkOpenBankingStatus } from './LinkOpenBankingStatus/LinkOpenBankingStatus';

export type TDocRequirementReview =
  GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse['data']['doc_requirement_reviews'][number];

type TProps = {
  requirement: TOfferDataRequirement['actions'][number];
  canUploadDocs: boolean;
  isLoading?: boolean;
  docRequirementReviews?: TDocRequirementReview[];
  hasDocumentFeedback?: boolean;
};

type TDocTypeStatus = {
  status: 'processing' | 'required' | 'completed';
  pillVariant: 'green' | 'london' | 'yellow' | 'red';
  labelText: string;
};

export const DocumentGroup = ({
  requirement,
  canUploadDocs,
  isLoading,
  docRequirementReviews,
  hasDocumentFeedback = false,
}: TProps) => {
  const { locale, isUK } = useLocale();
  const { application } = useGetIntroducerApplication();

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  const stateKey = application?.customer?.state_key;
  const { data: documents } = useGetDocuments(stateKey, hasDocumentFeedback);

  const { mutate: markAsDone } = useMarkFeedbackAsDone();

  // Below logic is mapping DE doc types to uk versions so we upload files to correct categories
  const documentType = handleDocumentTypeMapping(
    requirement.type as TDocumentTypeWithDE,
    locale,
  );

  const mappedRequirement = { ...requirement, type: documentType };

  useEffect(() => {
    if (documents) {
      const documentsForThisDocumentType = documents?.data?.filter(
        (uploadedDocument) => {
          return documentType === uploadedDocument.type;
        },
      );

      setAllFiles(documentsForThisDocumentType, hasDocumentFeedback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirement, documents, hasDocumentFeedback]);

  const docReviewsForType = docRequirementReviews?.filter(
    (review) =>
      review.resolution_method ===
      mappedRequirement.recommended_resolution_method,
  );

  const getMissingCoverages = (markedAsDone: boolean) =>
    docReviewsForType
      ?.filter((review) => review.marked_as_done === markedAsDone)
      .flatMap((review) => review.bank_account_coverages)
      .filter((coverage) => coverage.missing_coverages.length > 0);

  const rejectedCoverages = getMissingCoverages(false);
  const overriddenCoverages = getMissingCoverages(true);

  const hasFeedback =
    uploadedFiles.some(
      (file) => file.docsFeedback?.length && file.statusGroup === 'rejected',
    ) || !!rejectedCoverages?.length;

  const handleFeedbackOnUpload = useCallback(() => {
    const feedbackIds = uploadedFiles
      .filter((file) => file.status === 'rejected')
      .flatMap((file) => file.docsFeedback ?? [])
      .filter((feedback) => !feedback.marked_as_done_by_customer)
      .map((feedback) => feedback.id);

    const requirementIds = (docReviewsForType ?? [])
      .filter((requirement) => !requirement.marked_as_done)
      .map((requirement) => requirement.id);

    const allItems: TMarkFeedbackAsDoneItem[] = [
      { type: 'file_feedback', ids: feedbackIds },
      { type: 'requirement', ids: requirementIds },
    ];
    const itemsWithIds = allItems.filter(({ ids }) => ids.length > 0);

    if (itemsWithIds.length) {
      markAsDone(itemsWithIds);
    }
  }, [uploadedFiles, docReviewsForType, markAsDone]);

  const getDocTypeStatus = (): TDocTypeStatus | undefined => {
    if (hasFeedback && mappedRequirement.status !== 'completed') {
      return {
        status: 'required',
        pillVariant: 'red',
        labelText: t`Documents requested`,
      };
    }

    switch (mappedRequirement.status) {
      case 'pending':
        return {
          status: 'processing',
          pillVariant: 'london',
          labelText: t`Processing`,
        };
      case 'error':
      case 'required':
        return {
          status: 'required',
          pillVariant: 'red',
          labelText: t`Required`,
        };
      case 'completed':
        return {
          status: 'completed',
          pillVariant: 'green',
          labelText: t`Completed`,
        };
    }
  };

  const documentTypeStatus = getDocTypeStatus();
  const showOpenBanking = mappedRequirement.type === 'bank_statement' && isUK;

  const renderPill = (docType?: TDocTypeStatus) => {
    if (!docType) {
      return null;
    }

    return (
      <Pill
        className={clsx(styles.pill, styles[docType.status])}
        variant={docType.pillVariant}
      >
        {isLoading ? (
          <Spinner className={styles.spinner} size="small" />
        ) : (
          _.upperFirst(docType.labelText)
        )}
      </Pill>
    );
  };

  const getDocumentRequirementUploadType = (docType?: string) => {
    if (!docType) {
      return null;
    }

    return docType
      .split('_')
      .join(' ') as PostDocumentUploadResponse['data']['document_type'];
  };

  if (!stateKey) {
    return null;
  }

  return (
    <Accordion
      className={styles.accordion}
      titleClassName={styles.titleContainer}
      title={
        <>
          <p className={styles.title}>
            {getRequirementsTitle(mappedRequirement.type)}
            <span className={styles.optional}>
              {mappedRequirement.is_optional && ' (Optional)'}
            </span>
          </p>
          {!mappedRequirement.is_optional && renderPill(documentTypeStatus)}
        </>
      }
    >
      <>
        {documentTypeStatus?.status === 'required' && (
          <p className={styles.instruction}>
            {getInstruction(
              mappedRequirement.type,
              mappedRequirement.quantifier?.value,
              mappedRequirement.quantifier?.unit,
            )}
          </p>
        )}
        <UploadDropButton
          setFileLoading={setFileLoading}
          setFileSuccess={setFileSuccess}
          setFileError={setFileError}
          setFileReject={setFileReject}
          stateKey={stateKey}
          isOptional={mappedRequirement.is_optional}
          documentType={getDocumentRequirementUploadType(documentType)}
          isDisabled={!canUploadDocs}
          onSuccess={hasFeedback ? handleFeedbackOnUpload : undefined}
        />
        {FILE_STATUS_GROUPS.map((statusGroup) => {
          const missingCoverages =
            statusGroup === 'rejected'
              ? rejectedCoverages
              : statusGroup === 'overridden'
                ? overriddenCoverages
                : undefined;

          return (
            <FileStatusGroup
              key={`${mappedRequirement.type}-${statusGroup}`}
              testId={statusGroup}
              statusGroup={statusGroup}
              files={uploadedFiles}
              removeFile={removeFile}
              missingCoverages={missingCoverages}
            />
          );
        })}
        {showOpenBanking && <LinkOpenBankingStatus />}
      </>
    </Accordion>
  );
};
